'use client';

import { useParams } from 'next/navigation';
import type React from 'react';
import { Suspense, useEffect, useMemo, useRef } from 'react';

import type { Token } from '@/legacy/@types/Token';
import AlertBanner from '@/legacy/components/app/AlertBanner';
import ErrorBoundaryAlertBanner from '@/legacy/components/app/ErrorBoundaryAlertBanner';
import ErrorFetchingBranches from '@/legacy/components/app/ErrorFetchingBranches';
import Header from '@/legacy/components/app/Header';
import NavigationV2 from '@/legacy/components/app/NavigationV2';
import NoBranchAccess from '@/legacy/components/app/NoBranchAccess';
import GlobalQuickMenuWrapper from '@/legacy/components/app/QuickMenu/GlobalQuickMenuWrapper';
import RouteErrorBoundary from '@/legacy/components/app/RouteErrorBoundary';
import UnknownBranch from '@/legacy/components/app/UnknownBranch';
import useInitialized from '@/legacy/components/app/useInitialized';
import WrongMarket from '@/legacy/components/app/WrongMarket';
import useBranches from '@/legacy/data/branch/useBranches';
import useAuth from '@/legacy/hooks/useAuth';
import { useTemporaryAccess } from '@/legacy/providers/auth';
import Loader from '@/legacy/styled-components/components/Loader';
import { PortalElement, PortalNamespace } from '@/legacy/styled-components/components/Portal';
import {
  Root,
  RootBody,
  RootHeader,
  RootLoaderCenter,
  RootMain,
} from '@/legacy/styled-components/components/Root';
import { getMarketMetadata } from '@/legacy/utils/market';

const checkToken = (branchId: string, decodedToken?: Token) => {
  if (!decodedToken) return false;
  if (decodedToken?.role !== 'wayke-admin') return false;
  return typeof decodedToken['belongs-to-branch'] === 'string'
    ? decodedToken['belongs-to-branch'] !== branchId
    : !decodedToken['belongs-to-branch']?.includes(branchId);
};

interface AppBodyContentProps {
  children: React.ReactNode;
}

const AppBodyContent = ({ children }: AppBodyContentProps) => {
  const { loading, errorType, initialized } = useInitialized();

  const params = useParams();
  const branchId = params?.branchId as string | undefined;

  const { decodedToken } = useAuth();
  const [addTemporaryAccess, requestTemporaryAccess] = useTemporaryAccess();
  const [branches] = useBranches();
  const { applicationName } = getMarketMetadata();

  const canTryToAddTemporaryAccess = useMemo(
    () => branchId && checkToken(branchId, decodedToken),
    [branchId, decodedToken],
  );

  // Track if we're currently requesting access to prevent duplicate calls
  // (can happen with React Strict Mode or rapid re-renders during token refresh)
  const isRequestingAccess = useRef(false);

  useEffect(() => {
    if (canTryToAddTemporaryAccess && branchId && !isRequestingAccess.current) {
      isRequestingAccess.current = true;
      addTemporaryAccess(branchId).finally(() => {
        isRequestingAccess.current = false;
      });
    }
  }, [canTryToAddTemporaryAccess, branchId, addTemporaryAccess]);

  const currentBranch = useMemo(
    () => branches?.find((b) => b.id === branchId),
    [branches, branchId],
  );

  useEffect(() => {
    if (currentBranch?.name) {
      document.title = `${currentBranch?.name} | ${applicationName}`;
    }
  }, [currentBranch, applicationName]);

  const wrongMarket = errorType === 'wrong-market';

  return (
    <Root>
      <RootHeader>{!wrongMarket && <Header />}</RootHeader>
      <RootBody>
        {!wrongMarket && <NavigationV2 unknownBranch={!initialized} error={!!errorType} />}
        <RootMain id="main" tabIndex={-1}>
          <ErrorBoundaryAlertBanner>
            <AlertBanner />
          </ErrorBoundaryAlertBanner>
          <Suspense
            fallback={
              <RootLoaderCenter>
                <Loader />
              </RootLoaderCenter>
            }
          >
            {initialized ? (
              <>
                <GlobalQuickMenuWrapper />
                <RouteErrorBoundary>{children}</RouteErrorBoundary>
              </>
            ) : loading || requestTemporaryAccess.isLoading || canTryToAddTemporaryAccess ? (
              <RootLoaderCenter>
                <Loader />
              </RootLoaderCenter>
            ) : (
              errorType && (
                <>
                  {['wrong-market'].includes(errorType) && <WrongMarket />}
                  {['branches', 'branch', 'permissions'].includes(errorType) && (
                    <ErrorFetchingBranches />
                  )}
                  {['empty-branch-list'].includes(errorType) && <NoBranchAccess />}
                  {['unknown-branch'].includes(errorType) && <UnknownBranch />}
                </>
              )
            )}
          </Suspense>
          <PortalElement id={PortalNamespace.FloatingBar} />
        </RootMain>
      </RootBody>
    </Root>
  );
};

export default AppBodyContent;
